import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { BaseComponent } from '../../components/base/base.component';
import { SessionService } from '../../_services/session/session.service';
import { StatsFilterService, StatsFilters } from './statistics-filter.service';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

interface NavItem {
  labelKey: string;
  icon: string;
  route: string;
  descriptionKey?: string;
}

interface DatePresetOption {
  labelKey: string;
  value: 'today' | 'week' | 'month' | 'quarter' | 'year';
}

interface ComparisonOption {
  label: string;
  value: 'previous' | 'year_ago';
}

@Component({
  standalone: true,
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    DatePickerModule,
    SelectModule,
    TooltipModule,
    MenuModule,
    ToggleSwitchModule,
  ],
})
export class StatisticsComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private subscriptions: Subscription[] = [];

  // Navigation
  public navItems: NavItem[] = [
    {
      labelKey: 'STATISTICS.NAV.DASHBOARD',
      icon: 'pi-th-large',
      route: 'dashboard',
      descriptionKey: 'STATISTICS.NAV_DESCRIPTIONS.DASHBOARD',
    },
    {
      labelKey: 'STATISTICS.NAV.REVENUE',
      icon: 'pi-wallet',
      route: 'revenue',
      descriptionKey: 'STATISTICS.NAV_DESCRIPTIONS.REVENUE',
    },
    {
      labelKey: 'STATISTICS.NAV.TREATMENTS',
      icon: 'pi-heart',
      route: 'treatments',
      descriptionKey: 'STATISTICS.NAV_DESCRIPTIONS.TREATMENTS',
    },
    {
      labelKey: 'STATISTICS.NAV.PATIENTS',
      icon: 'pi-users',
      route: 'patients',
      descriptionKey: 'STATISTICS.NAV_DESCRIPTIONS.PATIENTS',
    },
    {
      labelKey: 'STATISTICS.NAV.APPOINTMENTS',
      icon: 'pi-calendar',
      route: 'appointments',
      descriptionKey: 'STATISTICS.NAV_DESCRIPTIONS.APPOINTMENTS',
    },
  ];

  // Filters state
  public filters: StatsFilters;
  public dateRangeValue: Date[] = [];
  public comparisonEnabled: boolean = true;
  public showMobileFilters: boolean = false;
  public showMobileNav: boolean = false;

  // Presets for date filter
  public datePresets: DatePresetOption[] = [
    { labelKey: 'STATISTICS.FILTERS.PRESETS.TODAY', value: 'today' },
    { labelKey: 'STATISTICS.FILTERS.PRESETS.WEEK', value: 'week' },
    { labelKey: 'STATISTICS.FILTERS.PRESETS.MONTH', value: 'month' },
    { labelKey: 'STATISTICS.FILTERS.PRESETS.QUARTER', value: 'quarter' },
    { labelKey: 'STATISTICS.FILTERS.PRESETS.YEAR', value: 'year' },
  ];

  // Comparison options
  public comparisonOptions: ComparisonOption[] = [
    { label: 'STATISTICS.FILTERS.COMPARISON.PREVIOUS', value: 'previous' },
    { label: 'STATISTICS.FILTERS.COMPARISON.YEAR_AGO', value: 'year_ago' },
  ];

  public selectedComparison: string = 'previous';

  constructor(
    public session: SessionService,
    public translate: TranslateService,
    public filterService: StatsFilterService,
  ) {
    super(session);
  }

  ngOnInit(): void {
    super.ngOnInit();

    // Subscribe to filter changes
    this.subscriptions.push(
      this.filterService.filters$.subscribe((filters) => {
        this.filters = filters;
        this.dateRangeValue = [filters.dateRange.start, filters.dateRange.end];
        this.comparisonEnabled = filters.comparisonEnabled;
        this.selectedComparison = filters.comparisonType || 'previous';
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // =========================================================================
  // DATE FILTER METHODS
  // =========================================================================

  setPreset(preset: string): void {
    switch (preset) {
      case 'today':
        this.filterService.setToday();
        break;
      case 'week':
        this.filterService.setThisWeek();
        break;
      case 'month':
        this.filterService.setThisMonth();
        break;
      case 'quarter':
        this.filterService.setThisQuarter();
        break;
      case 'year':
        this.filterService.setThisYear();
        break;
    }
  }

  onDateRangeChange(): void {
    // Solo actualizar cuando ambas fechas estén seleccionadas y sean válidas
    if (
      this.dateRangeValue?.length === 2 &&
      this.dateRangeValue[0] instanceof Date &&
      this.dateRangeValue[1] instanceof Date &&
      !isNaN(this.dateRangeValue[0].getTime()) &&
      !isNaN(this.dateRangeValue[1].getTime())
    ) {
      console.log('📅 [FILTER] Date range changed:', this.dateRangeValue);
      this.filterService.setCustomRange(
        this.dateRangeValue[0],
        this.dateRangeValue[1],
      );
    }
  }

  shiftPeriod(direction: 'previous' | 'next'): void {
    if (!this.filters?.activePreset) return;
    this.filterService.shiftActivePeriod(direction);
  }

  get canShiftPeriod(): boolean {
    return !!this.filters?.activePreset;
  }

  // =========================================================================
  // COMPARISON METHODS
  // =========================================================================

  toggleComparison(): void {
    if (this.comparisonEnabled) {
      this.filterService.enableComparison(
        this.selectedComparison as 'previous' | 'year_ago',
      );
    } else {
      this.filterService.disableComparison();
    }
  }

  onComparisonTypeChange(): void {
    if (this.comparisonEnabled) {
      this.filterService.enableComparison(
        this.selectedComparison as 'previous' | 'year_ago',
      );
    }
  }

  // =========================================================================
  // MOBILE
  // =========================================================================

  toggleMobileFilters(): void {
    this.showMobileFilters = !this.showMobileFilters;
  }

  toggleMobileNav(): void {
    this.showMobileNav = !this.showMobileNav;
  }

  closeMobileNav(): void {
    this.showMobileNav = false;
  }

  // =========================================================================
  // UTILITY
  // =========================================================================

  get dateRangeLabel(): string {
    return this.filterService.getDateRangeLabel();
  }

  get activeFilterCount(): number {
    return this.filterService.getActiveFilterCount();
  }
}
